import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Caption1, MessageBar, MessageBarBody, Spinner, Text, mergeClasses } from '@fluentui/react-components';
import { PopupDialog } from '@vigeo/ui';
import {
  portalFormGroupHeading, portalFormGroups,
  type Person, type PortalFormGroupId, type PortalLinkStatus,
} from '@vigeo/shared';
import { communicationStatusPresenters, fetchPersonTimeline } from '@/features/timeline';
import { emailPortalLink, fetchPersonSteps, fetchPortalLinkStatus, revokePortalLink } from '@/lib/api';
import { PortalLinkButton, PortalLinkDialog, usePortalLinkHandOver } from '../PortalLinkDialog.js';
import { usePersonStyles } from './styles.js';

/**
 * STEP 3 — PORTAL. Frame `278:6492`, the four blocks it draws, in its order: the link and what HR
 * can do about it, when the candidate opened it, what the candidate sees, and the emails sent.
 *
 * WHY THIS STEP EXISTED ONLY ON THE RAIL UNTIL NOW. The link-and-code control was built early,
 * before the 06b rail, and landed in step 4 under a "Candidate portal access" disclosure. Step 3
 * was drawn on the rail unlinked. So the one screen whose whole subject is the portal pointed
 * nowhere, and HR's one move on a waiting candidate was two steps away from the step named after
 * it — which is Julio's first item in #462 §5. The control is MOVED here rather than copied:
 * `DocumentsStep` no longer renders it.
 *
 * WHAT IS BACKED AND WHAT IS NOT. The Link block reads `GET people/{id}/portal-link` (#471), which
 * carries no token and no access code; a status display must not read a credential. Two things the
 * frame draws have no store behind them, and both say so on screen rather than being faked:
 *
 *   - "when the candidate LAST opened it" — the token records `firstUsedAt`, the first use only
 *     (`portal.ts`, written once under an `IS_NULL` condition). The block reports first use and
 *     names it as such.
 *   - "Emails sent" cannot be narrowed to the portal's own emails. A stored message carries a
 *     subject, a status and its text, and no field saying which template it came from, so there is
 *     nothing to filter on. The block shows this person's messages and says which ones they are.
 */

export interface PortalStepProps {
  readonly person: Person;
  readonly canEdit: boolean;
  /** From the record's own workflow read, so this step adds no second request for it. */
  readonly hasOffer: boolean;
}

const STATE_WORDS: Record<PortalLinkStatus['state'], { readonly label: string; readonly tone: 'informative' | 'success' | 'warning' | 'danger' }> = {
  none: { label: 'No link yet', tone: 'informative' },
  active: { label: 'Active', tone: 'success' },
  expired: { label: 'Expired', tone: 'warning' },
  revoked: { label: 'Revoked', tone: 'danger' },
};

const day = (iso: string) => new Date(iso).toLocaleDateString();
const moment = (iso: string) => new Date(iso).toLocaleString();

export function PortalStep({ person, canEdit, hasOffer }: PortalStepProps) {
  const styles = usePersonStyles();
  const client = useQueryClient();
  const first = person.name.preferredName || person.name.givenName;
  const key = ['portal-link', person.id];
  const status = useQuery({ queryKey: key, queryFn: () => fetchPortalLinkStatus(person.id) });
  const refresh = () => { void client.invalidateQueries({ queryKey: key }); };

  /*
   * Emailing and revoking both change the link, so both end by re-reading the status rather than
   * patching a copy of it. The server's answer is the only thing that knows what the link is now:
   * issuing revokes whatever was live, and a revoke of nothing returns `revoked: false`.
   */
  const email = useMutation({ mutationFn: () => emailPortalLink(person.id), onSettled: refresh });
  const revoke = useMutation({ mutationFn: () => revokePortalLink(person.id), onSettled: refresh });
  const busy = email.isPending || revoke.isPending;

  /*
   * THE POP-UP RULES, as Julio approved them on 2026-10-06 (#502, Figma `1038:2`). Three controls
   * here change the candidate's access and all three now say so before they act.
   *
   * The by-hand link and code moved to the SHARED dialog (#501, PG5), which is also why the
   * `<details>` disclosure that used to hold it is gone: it existed only because an inline panel
   * revealed a code that cannot be shown twice, so nobody should trip over it while reading the
   * status. Behind the dialog's own question, the code only appears once somebody has asked for it.
   */
  const handOver = usePortalLinkHandOver(person, true);
  /*
   * `PopupDialog` remembers the opener itself — a layout effect reads `document.activeElement`
   * before Fluent moves the keyboard — so these are plain open/closed flags. `useRiskyQuestion`
   * existed to carry the opener and is gone with its component (#502 CQ).
   */
  const [askingRevoke, setAskingRevoke] = useState(false);
  const [askingEmail, setAskingEmail] = useState(false);

  /**
   * A LINK THAT IS LIVE is what makes these questions worth asking (PG3's own wording).
   *
   * With no link, or an expired or revoked one, nothing that works is being destroyed, and a
   * confirm that fires when nothing is at stake is the kind people learn to click through — which
   * makes the one that matters cheaper to dismiss.
   */
  const linkIsLive = status.data?.state === 'active';

  /*
   * The status block is stale the instant a link is issued: `issuedAt` and `expiresAt` both move,
   * and the badge is the first thing HR reads. `usePortalLinkHandOver` reports the link rather than
   * taking a callback, so the re-read hangs off the token appearing. Keyed on the token so a second
   * link re-reads too, and so clearing the reveal does not.
   */
  const issuedToken = handOver.issued?.token ?? null;
  useEffect(() => {
    if (issuedToken !== null) refresh();
    // `refresh` closes over the query client, which is stable for the life of the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuedToken]);

  return <div className={styles.page}>
    {/*
      NO PAGE HEADING HERE. The frame's "Portal" title and its sentence underneath are what
      `SectionWorkspace` already draws from the section's `label` and `description`, so adding them
      again put the word "Portal" on the screen twice — which only a screenshot showed.
    */}
    <section className={styles.labelledRow} aria-labelledby="portal-link-heading">
      <div className={styles.rowLabel}>
        <h4 id="portal-link-heading" className={styles.h}>Link</h4>
        <Caption1>The access code isn’t shown again. If it’s lost, create a new link — the old one stops working.</Caption1>
      </div>
      <div className={styles.rowBody}>
      {status.data === undefined ? null : <Badge appearance="filled" color={STATE_WORDS[status.data.state].tone}>{STATE_WORDS[status.data.state].label}</Badge>}

      {status.isPending ? <Spinner size="tiny" label="Reading the portal link" />
        : status.isError ? <MessageBar intent="error"><MessageBarBody>The portal link’s status could not be read. {status.error.message}</MessageBarBody></MessageBar>
          : <LinkFacts status={status.data} first={first} />}

      {email.data === undefined ? null : (
        <MessageBar intent={email.data.delivered ? 'success' : 'warning'}>
          <MessageBarBody>{email.data.delivered
            ? `A new link went to ${email.data.maskedTo}. The old one stopped working.`
            : `A new link exists and the email did not go out: ${email.data.reason ?? 'no reason was given'}. Hand it over below instead.`}</MessageBarBody>
        </MessageBar>
      )}
      {email.isError ? <MessageBar intent="error"><MessageBarBody>{email.error.message}</MessageBarBody></MessageBar> : null}
      {revoke.data?.revoked === false ? <MessageBar intent="info"><MessageBarBody>There was no live link to revoke.</MessageBarBody></MessageBar> : null}
      {revoke.isError ? <MessageBar intent="error"><MessageBarBody>{revoke.error.message}</MessageBarBody></MessageBar> : null}

      {/*
        A viewer gets the status and no buttons. Every control here changes the candidate's access,
        which is `portal-link.issue` and `portal-link.revoke` — both Admin — while reading the
        status is not (`auth/policy.ts`). The API enforces all three; this only avoids offering a
        Viewer a button that would come back 403.
      */}
      {canEdit ? <div className={styles.chips}>
        {/*
          EMAILING ASKS WHEN A LINK IS LIVE, and goes straight out otherwise. Issuing revokes
          whatever was live — the endpoint answers `revokedPrevious` — so this button kills the
          candidate's working link exactly as "Make a new link" does; only the delivery differs.
          PG3 names the other one, and a rule that asks on one and not the other protects the
          candidate from the button HR presses less often (decision recorded on #502).
        */}
        <Button
          appearance="primary"
          disabled={busy}
          onClick={() => { if (linkIsLive) setAskingEmail(true); else email.mutate(); }}
        >
          Email a new link
        </Button>
        <Button disabled={busy || !linkIsLive} onClick={() => setAskingRevoke(true)}>Revoke link</Button>
        {/*
          The by-hand path, and today the ONLY one that reaches a candidate: mail is off by
          configuration in both environments, so "Email a new link" answers `delivered: false`.
        */}
        <PortalLinkButton handOver={handOver} appearance="subtle" />
      </div> : null}

      {canEdit ? <>
        <PortalLinkDialog handOver={handOver} firstName={first} />

        <PopupDialog
          open={askingRevoke}
          title={`Revoke ${first}'s link?`}
          startOn="cancel"
          busy={revoke.isPending}
          onClose={() => setAskingRevoke(false)}
          primary={{ label: 'Revoke the link', busyLabel: 'Revoking…', onClick: () => { setAskingRevoke(false); revoke.mutate(); } }}
        >
          <Text>{`${first} will not be able to sign in with the link or the access code they have. Anything already submitted is kept. To let ${first} back in you will have to create a new link.`}</Text>
        </PopupDialog>

        <PopupDialog
          open={askingEmail}
          title={`Email ${first} a new link?`}
          startOn="cancel"
          busy={email.isPending}
          onClose={() => setAskingEmail(false)}
          primary={{ label: 'Send the new link', busyLabel: 'Sending…', onClick: () => { setAskingEmail(false); email.mutate(); } }}
        >
          <Text>{`This replaces the link ${first} already has, so the one they were sent stops working. If ${first} is signing right now, they will be interrupted and will have to start again from the new email.`}</Text>
        </PopupDialog>
      </> : null}
      </div>
    </section>

    <section className={styles.labelledRow} aria-labelledby="portal-activity-heading">
      <div className={styles.rowLabel}>
        <h4 id="portal-activity-heading" className={styles.h}>Candidate activity</h4>
        <Caption1>From the portal sign-in record.</Caption1>
      </div>
      <div className={styles.rowBody}>
        {status.data === undefined ? null : status.data.firstUsedAt === null
          ? <Text>{status.data.state === 'none' ? `${first} has no link to open yet.` : 'Not opened yet.'}</Text>
          : <Text>{`${first} first opened the link on ${moment(status.data.firstUsedAt)}.`}</Text>}
        {/*
          The frame prints "Not opened yet — sent 2 days ago", and the sign-in record holds only
          the FIRST use (`portal.ts` writes `firstUsedAt` once, under an `IS_NULL` condition). So
          the unopened sentence matches the frame exactly, and once a link HAS been opened this
          says which opening it is reporting rather than implying the latest.
        */}
        <Caption1>Only the first opening is recorded. There is no record of later visits.</Caption1>
      </div>
    </section>

    <WhatTheySee personId={person.id} first={first} hasOffer={hasOffer} />
    <EmailsSent personId={person.id} first={first} />
  </div>;
}

/** The dates the frame prints under the status word. Absent dates are absent, never zeroed. */
function LinkFacts({ status, first }: { readonly status: PortalLinkStatus; readonly first: string }) {
  if (status.state === 'none') return <Text>{`${first} has never been sent a portal link.`}</Text>;
  return <>
    {status.issuedAt === null ? null : <Text>{`Issued ${day(status.issuedAt)}.`}</Text>}
    {status.state === 'revoked' && status.revokedAt !== null ? <Text>{`Revoked ${day(status.revokedAt)}. ${first} cannot sign in with it.`}</Text>
      : status.expiresAt === null ? null
        : <Text>{status.state === 'expired' ? `Expired ${day(status.expiresAt)}.` : `Expires ${day(status.expiresAt)}.`}</Text>}
  </>;
}

/**
 * What the candidate meets, in the order they meet it: the offer first, then the forms grouped the
 * way 3.1 groups them — in the order the API sent them, which it has already put through
 * `orderPortalForms`, so HR's list and the candidate's "Form 4 of 22" name the same form.
 *
 * The groups are THIS PERSON'S, counted from their own issued forms. A group nobody has is not
 * listed: a heading with nothing under it reads as something waiting to happen.
 */
function WhatTheySee({ personId, first, hasOffer }: { readonly personId: string; readonly first: string; readonly hasOffer: boolean }) {
  const styles = usePersonStyles();
  const steps = useQuery({ queryKey: ['person-steps', personId], queryFn: () => fetchPersonSteps(personId) });
  const has = new Set((steps.data ?? []).map((step) => step.portalGroup).filter((group): group is string => group !== null));
  const groups = portalFormGroups.filter((group) => has.has(group.id));

  return <section className={styles.labelledRow} aria-labelledby="portal-sees-heading">
    <div className={styles.rowLabel}>
      <h4 id="portal-sees-heading" className={styles.h}>{`What ${first} sees`}</h4>
      <Caption1>In this order. Onboarding steps open once the offer is signed.</Caption1>
    </div>
    <div className={styles.rowBody}>
      {/*
        A NUMBERED LIST OF NAMED ROWS, each with a chip, as the frame draws it — the offer as 1,
        then one row per group this person actually has. It was group headings with a count each,
        which is step 4's shape, not this frame's.

        PAUL'S DESCRIPTIVE TAILS ARE NOT HERE. The frame reads "Application — personal details,
        work history, references" and "Uploads — license, CPR/BLS card, TB result, driver's
        licence". That copy exists nowhere in the catalog — it is written by hand per frame, the
        same wall #357 hit — so each row carries the group's own label and the tail is listed on
        the ticket rather than invented.
      */}
      <ol className={mergeClasses(styles.recordList, styles.numberedList)}>
        <li className={mergeClasses(styles.recordRow, styles.numberedRow)}>
          <Text weight="semibold">{hasOffer ? 'Review and sign your offer' : 'The offer, once one is drafted'}</Text>
          <Badge appearance="outline" shape="rounded" color={hasOffer ? 'warning' : 'informative'}>{hasOffer ? 'To do' : 'Not drafted yet'}</Badge>
        </li>
        {steps.isPending ? <li><Spinner size="tiny" label="Reading this person’s forms" /></li>
          : steps.isError ? <li><MessageBar intent="error"><MessageBarBody>The form list could not be read. {steps.error.message}</MessageBarBody></MessageBar></li>
            : groups.length === 0 ? <li><Text>No forms have been issued yet. They arrive when this candidate moves to onboarding.</Text></li>
              : groups.map((group) => <li key={group.id} className={mergeClasses(styles.recordRow, styles.numberedRow)}>
                <Text>{portalFormGroupHeading(group.id as PortalFormGroupId, [])}</Text>
                <Badge appearance="outline" shape="rounded" color="informative">After the offer</Badge>
              </li>)}
      </ol>
      {/*
        SAID OUT LOUD BECAUSE THE SCREEN WOULD OTHERWISE CLAIM A GATE THAT DOES NOT EXIST — and
        the frame says it too, in these words: nothing today stops a candidate opening an
        onboarding form before the offer is signed.
      */}
      <Caption1>Holding onboarding steps until the offer is signed is planned app work; today HR can issue them earlier.</Caption1>
    </div>
  </section>;
}

/**
 * The messages this person has been sent.
 *
 * NOT narrowed to the portal's own emails, and that is a limit rather than a choice: a stored
 * message carries a subject, a status and its text, with no field naming the template it came
 * from (`timelineCommunicationSchema`). Narrowing by subject text would be a guess that breaks
 * the first time a template's wording changes, so the heading says what the list actually is.
 */
function EmailsSent({ personId, first }: { readonly personId: string; readonly first: string }) {
  const styles = usePersonStyles();
  const timeline = useQuery({ queryKey: ['person-timeline', personId], queryFn: () => fetchPersonTimeline(personId) });
  const messages = (timeline.data?.entries ?? []).filter((entry) => entry.kind === 'communication');

  return <section className={styles.labelledRow} aria-labelledby="portal-emails-heading">
    <div className={styles.rowLabel}>
      <h4 id="portal-emails-heading" className={styles.h}>Emails sent</h4>
      <Caption1>{`Every message sent to ${first} — the portal's own emails are not recorded separately.`}</Caption1>
    </div>
    <div className={styles.rowBody}>
    {timeline.isPending ? <Spinner size="tiny" label="Reading the messages" />
      : timeline.isError ? <MessageBar intent="error"><MessageBarBody>The messages could not be read. {timeline.error.message}</MessageBarBody></MessageBar>
        : messages.length === 0 ? <Text>Nothing has been emailed to {first} yet.</Text>
          : <ul className={styles.recordList}>{messages.map((entry) => <li key={entry.id} className={styles.recordRow}>
            <div className={styles.recordMain}>
              <Text weight="semibold">{entry.communication.subject}</Text>
              <Caption1>{moment(entry.communication.sentAt ?? entry.communication.queuedAt)}</Caption1>
            </div>
            <Badge appearance="outline" shape="rounded">{communicationStatusPresenters[entry.communication.status].label}</Badge>
          </li>)}</ul>}
    </div>
  </section>;
}
